import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  output,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ChatbotService } from '../chatbot.service';
import { MapGroup, MapSection } from '../chatbot.types';
import { MenuItem } from '../../../modules/nav-module/navbar-side/navbar-menu';
import { SessionDataService } from '../../../services/shared/SessionDataService.service';

/** One section together with its direct child sub-sections (one level deep). */
interface SectionNode {
  section: TreeSection;
  children: TreeSection[];
}

type TreeSection = Omit<MapSection, 'group'> & { group: string };

/** One display group shown in the visual tree. */
interface TreeGroup {
  id: string;
  labelKey: string;
  icon: string;
  sections: SectionNode[];
  /** Total number of visible items (parents + children) — drives the wide layout. */
  totalItems: number;
}

const GROUP_META: ReadonlyArray<{ id: MapGroup; labelKey: string; icon: string }> = [
  { id: 'dashboard',     labelKey: 'chatbot.map.group-dashboard',     icon: 'fa-home' },
  { id: 'telecontrol',   labelKey: 'chatbot.map.group-telecontrol',   icon: 'fa-binoculars' },
  { id: 'alarms',        labelKey: 'chatbot.map.group-alarms',        icon: 'fa-bell' },
  { id: 'devices',       labelKey: 'chatbot.map.group-devices',       icon: 'fa-microchip' },
  { id: 'devices-ops',   labelKey: 'chatbot.map.group-devices-ops',   icon: 'fa-file-upload' },
  { id: 'gateways',      labelKey: 'chatbot.map.group-gateways',      icon: 'fa-broadcast-tower' },
  { id: 'orders',        labelKey: 'chatbot.map.group-orders',        icon: 'fa-clipboard-list' },
  { id: 'data-analysis', labelKey: 'chatbot.map.group-data-analysis', icon: 'fa-chart-bar' },
  { id: 'data-mgmt',     labelKey: 'chatbot.map.group-data-mgmt',     icon: 'fa-database' },
  { id: 'files',         labelKey: 'chatbot.map.group-files',         icon: 'fa-file-export' },
  { id: 'sensory',       labelKey: 'chatbot.map.group-sensory',       icon: 'fa-wave-square' },
  { id: 'network',       labelKey: 'chatbot.map.group-network',       icon: 'fa-network-wired' },
  { id: 'maint-global',  labelKey: 'chatbot.map.group-maint-global',  icon: 'fa-globe' },
  { id: 'maint-entity',  labelKey: 'chatbot.map.group-maint-entity',  icon: 'fa-sitemap' },
  { id: 'admin',         labelKey: 'chatbot.map.group-admin',         icon: 'fa-cog' },
  { id: 'users',         labelKey: 'chatbot.map.group-users',         icon: 'fa-users' },
  { id: 'install',       labelKey: 'chatbot.map.group-install',       icon: 'fa-toolbox' },
  { id: 'subscriber',    labelKey: 'chatbot.map.group-subscriber',    icon: 'fa-user-circle' },
];

/**
 * Dialog showing the chatbot's application map as sections grouped by area, plus navbar menu routes missing from the map.
 * Emits `navigate` with the chosen section payload and `closed` on dismiss.
 */
@Component({
  selector: 'app-chatbot-app-tree',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chatbot-app-tree.component.html',
  styleUrl: './chatbot-app-tree.component.scss',
  imports: [TranslateModule],
})
export class ChatbotAppTreeComponent {
  private readonly service = inject(ChatbotService);
  private readonly sessionDataService = inject(SessionDataService);

  /** Emitted when the user requests to close the dialog. */
  readonly closed = output<void>();

  /** Emitted when the user clicks "Go" on a section. */
  readonly navigate = output<string>();

  private readonly dialogRef = viewChild<ElementRef<HTMLDialogElement>>('dialogEl');

  protected readonly groups = computed<TreeGroup[]>(() => {
    const allSections: TreeSection[] = this.service.sectionMap();
    const visibleIds = new Set(allSections.map(s => s.id));
    const groups: TreeGroup[] = GROUP_META
      .map(meta => {
        const groupSections = allSections.filter(s => s.group === meta.id);
        const sections: SectionNode[] = groupSections
          .filter(s => !s.parentId || !visibleIds.has(s.parentId))
          .map(s => ({
            section: s,
            children: groupSections.filter(c => c.parentId === s.id),
          }));
        const totalItems = sections.reduce((sum, node) => sum + 1 + node.children.length, 0);
        return { ...meta, sections, totalItems };
      });

    const mappedPayloads = new Set(allSections.map(section => this.normalizePayload(section.payload)));
    const addMenuRoutes = (items: MenuItem[], root: MenuItem): void => {
      for (const item of items) {
        const payload = item.link ? this.normalizePayload(item.link) : '';
        if (payload && !mappedPayloads.has(payload)) {
          mappedPayloads.add(payload);
          const mappedSection = allSections.find(section => {
            const sectionPayload = this.normalizePayload(section.payload);
            return payload === sectionPayload || payload.startsWith(`${sectionPayload}/`);
          });
          const mappedGroup = GROUP_META.find(group => group.id === mappedSection?.group);
          const parentName = root.name || item.name || payload;
          const groupId = mappedGroup?.id ?? `menu:${parentName}`;
          let group = groups.find(candidate => candidate.id === groupId);
          if (!group) {
            group = {
              id: groupId,
              labelKey: mappedGroup?.labelKey ?? parentName,
              icon: mappedGroup?.icon ?? this.iconClass(root.icon),
              sections: [],
              totalItems: 0,
            };
            groups.push(group);
          }
          group.sections.push({
            section: {
              id: `menu:${payload}`,
              icon: this.iconClass(item.icon),
              labelKey: item.name || payload,
              descKey: payload,
              payload,
              group: groupId,
              profiles: item.profile ?? [],
            },
            children: [],
          });
        }
        if (item.submenu?.length) {
          addMenuRoutes(item.submenu, root);
        }
      }
    };

    for (const item of this.sessionDataService.navbarMenu() ?? []) {
      addMenuRoutes([item], item);
    }

    return groups
      .filter(group => group.sections.length > 0)
      .map(group => ({
        ...group,
        totalItems: group.sections.reduce((sum, node) => sum + 1 + node.children.length, 0),
      }));
  });

  // Why effect(): showModal() is an imperative DOM API — not a derived value.
  // effect() tracks the viewChild signal and fires as soon as the dialog
  // element is connected to the DOM, so showModal() runs against a live element
  // in both zoned and zoneless change detection.
  private readonly _openModal = effect(() => {
    const el = this.dialogRef()?.nativeElement;
    if (el && !el.open) { el.showModal(); }
  });

  protected close(): void {
    this.dialogRef()?.nativeElement?.close();
    this.closed.emit();
  }

  protected goTo(payload: string): void {
    this.navigate.emit(payload);
  }

  /**
   * Close when the user clicks the backdrop (the area outside the dialog card).
   * The backdrop is the <dialog> element itself in the top layer; a click on the
   * card interior targets a child element, so `event.target === dialogEl` only
   * when the backdrop is clicked.
   */
  protected onDialogClick(event: MouseEvent, dialogEl: HTMLDialogElement): void {
    if (event.target === dialogEl) {
      dialogEl.close();
      this.closed.emit();
    }
  }

  /**
   * Intercept the browser's native Escape behaviour so Angular (not the browser)
   * controls the component lifecycle via the `closed` output.
   */
  protected onCancel(event: Event): void {
    event.preventDefault();
    this.dialogRef()?.nativeElement?.close();
    this.closed.emit();
  }

  private normalizePayload(payload: string): string {
    return payload.replace(/^\/+|\/+$/g, '');
  }

  private iconClass(icon: string | null): string {
    return icon?.split(/\s+/).find(name => name.startsWith('fa-') && name !== 'fa-fw') ?? 'fa-circle';
  }
}
